// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/* База API для прямых URL (картинки и т.п.) — тот же адрес, что у клиента API */
export const apiBaseUrl = API_BASE_URL;

// Types
export interface User {
  id: number;
  username: string;
  email?: string;
  note?: string;
  isAdmin: boolean;
  isApproved: boolean;
  parentUserId?: number | null;
  isChild?: boolean;
  createdAt?: string;
  lastLoginAt?: string;
}

export interface Mailbox {
  id: string;
  clientRequestId?: string | null;
  name: string;
  account?: string;
  hasPassword: boolean;
  isShared?: boolean;
  isAssigned?: boolean;
  isOwner?: boolean;
  canTransfer?: boolean;
  ownerRelation?: {
    type: 'self' | 'child' | 'assigned_children';
    color?: string | null;
    label?: string | null;
    avatarUrl?: string | null;
    children?: Array<{
      id: number;
      username?: string | null;
      telegramId?: string | null;
      color?: string | null;
      label?: string | null;
      avatarUrl?: string | null;
    }>;
    kingReceivesMetaNotifications?: boolean;
  } | null;
  emailCount: number;
  createdAt: string;
  lastChecked?: string;
  lastEmailAt?: string | null;
}

export interface MailboxTransferChild {
  id: number;
  username?: string | null;
  telegramId?: string | null;
  color?: string | null;
  label?: string | null;
  avatarUrl?: string | null;
  assigned?: boolean;
}

export interface MailboxTransferOptions {
  mailbox: {
    id: string;
    name: string;
    account?: string | null;
    ownerUserId: number;
  };
  mode: 'assign' | 'reclaim';
  children: MailboxTransferChild[];
  assignedChildUserIds: number[];
  kingReceivesMetaNotifications: boolean;
}

export interface Email {
  id: string;
  fromEmail: string;
  fromName?: string;
  toEmail: string;
  subject: string;
  body: string;
  verificationCode?: string | null;
  isRead: boolean;
  receivedAt: string;
  createdAt: string;
}

export interface TelegramWhitelistItem {
  telegramId: string;
  isActive: boolean;
  lastActiveAt?: string | null;
  createdAt: string;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  photoUrl?: string | null;
  updatedAt?: string | null;
  lastError?: string | null;
  children?: Array<{
    telegramId: string;
    username?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    photoUrl?: string | null;
    color?: string | null;
    displayName?: string | null;
    isActive?: boolean | null;
    lastError?: string | null;
  }>;
  displayName?: string | null;
  isChild?: boolean;
  kingTelegramId?: string | null;
  lastMailboxAt?: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  details?: any[];
}

export interface AiStatus {
  configured: boolean;
  isAvailable: boolean;
  isCritical: boolean;
  isLow: boolean;
  shouldShowBanner: boolean;
  message: string;
  currency: string;
  totalBalance: number;
  grantedBalance: number;
  toppedUpBalance: number;
  estimatedChecksAtRealUsage: number;
  estimatedChecksAtHeavyUsage: number;
  checkedAt: string;
  error?: string | null;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface PaginationData<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// API Client Class
class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private isLoggingOut: boolean = false;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.loadToken();
  }

  private loadToken() {
    this.token = localStorage.getItem('token');
  }

  private saveToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  private removeToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  clearAuthStorage() {
    this.removeToken();
    localStorage.removeItem('mailspace_user');
    localStorage.removeItem('mailspace_token');
    localStorage.removeItem('mailspace_mailbox_access_tokens');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      defaultHeaders.Authorization = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const data: any = isJson ? await response.json() : await response.text();

      if (!response.ok) {
        // Handle authentication errors - but not for mailbox password verification
        if (response.status === 401 && !url.includes('/verify-password') && !this.isLoggingOut) {
          this.isLoggingOut = true;
          console.log('🚫 401 ошибка - очищаем токены и выходим без перезагрузки страницы');
          this.clearAuthStorage();
          window.dispatchEvent(new Event('mailspace-auth-cleared'));
          // Сбрасываем флаг через секунду
          setTimeout(() => {
            this.isLoggingOut = false;
          }, 1000);
          // НЕ ДЕЛАЕМ window.location.href = '/' - это вызывает бесконечный цикл!
        }
        const serverMessage =
          (isJson && data && typeof data === 'object' && 'message' in data) ? data.message :
          (!isJson && typeof data === 'string' ? data : null);
        throw new Error(serverMessage || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication
  async register(userData: {
    username: string;
    password: string;
    confirmPassword: string;
    note?: string;
  }): Promise<ApiResponse<{ id: number; username: string; isApproved: boolean }>> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials: {
    username: string;
    password: string;
  }): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.success && response.data?.token) {
      this.saveToken(response.data.token);
    }

    return response;
  }

  async telegramAuth(telegramData: {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    auth_date: number;
    hash: string;
  }): Promise<{ success: boolean; token: string; user: any }> {
    const response = await this.request('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify(telegramData),
    });

    // Ответ от Telegram auth приходит напрямую, не в формате ApiResponse
    const telegramResponse = response as any;
    if (telegramResponse.success && telegramResponse.token) {
      this.saveToken(telegramResponse.token);
    }

    return telegramResponse;
  }

  async telegramLogin(telegramData: {
    telegram_id: string;
    username?: string;
  }): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>('/auth/telegram-login', {
      method: 'POST',
      body: JSON.stringify(telegramData),
    });

    if (response.success && response.data?.token) {
      this.saveToken(response.data.token);
    }

    return response;
  }

  async getProfile(): Promise<ApiResponse<{ user: User }>> {
    return this.request('/auth/me');
  }

  async changePassword(passwordData: {
    currentPassword: string;
    password: string;
    confirmPassword: string;
  }): Promise<ApiResponse<void>> {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.request<void>('/auth/logout', {
      method: 'POST',
    });
    this.clearAuthStorage();
    return response;
  }

  // Mailboxes
  async getMailboxes(params: {
    scope?: 'mine' | 'all';
    q?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<{ mailboxes: Mailbox[]; pagination?: any }>> {
    const scope = params.scope || 'mine';
    const q = (params.q || '').trim();
    const page = params.page || 1;
    const limit = params.limit || 30;

    const search = new URLSearchParams();
    if (scope === 'all') search.set('scope', 'all');
    if (q) search.set('q', q);
    search.set('page', String(page));
    search.set('limit', String(limit));

    const qs = search.toString();
    return this.request(`/mailboxes${qs ? `?${qs}` : ''}`);
  }

  async getMailbox(id: string, scope: 'mine' | 'all' = 'mine'): Promise<ApiResponse<{ mailbox: Mailbox }>> {
    const qs = scope === 'all' ? '?scope=all' : '';
    return this.request(`/mailboxes/${id}${qs}`);
  }

  async createMailbox(mailboxData: {
    name?: string;
    account?: string;
    password?: string;
    notePrivate?: boolean;
    clientRequestId?: string;
  }): Promise<ApiResponse<{ mailbox: Mailbox }>> {
    return this.request('/mailboxes', {
      method: 'POST',
      body: JSON.stringify(mailboxData),
    });
  }

  async verifyMailboxPassword(
    mailboxId: string,
    password: string
  ): Promise<ApiResponse<{ mailboxAccessToken: string }>> {
    return this.request(`/mailboxes/${mailboxId}/verify-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async updateMailbox(
    mailboxId: string,
    updateData: {
      password?: string;
      newPassword?: string;
    }
  ): Promise<ApiResponse<void>> {
    return this.request(`/mailboxes/${mailboxId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async deleteMailbox(
    mailboxId: string,
    password?: string
  ): Promise<ApiResponse<void>> {
    return this.request(`/mailboxes/${mailboxId}`, {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  }

  async getMailboxTransferOptions(mailboxId: string, q = ''): Promise<ApiResponse<MailboxTransferOptions>> {
    const search = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    return this.request(`/mailboxes/${mailboxId}/transfer-options${search}`);
  }

  async assignMailboxChildren(
    mailboxId: string,
    childUserIds: number[],
    kingReceivesMetaNotifications: boolean
  ): Promise<ApiResponse<{
    mailboxId: string;
    assignedChildUserIds: number[];
    kingReceivesMetaNotifications: boolean;
  }>> {
    return this.request(`/mailboxes/${mailboxId}/child-assignments`, {
      method: 'POST',
      body: JSON.stringify({ childUserIds, kingReceivesMetaNotifications })
    });
  }

  async reclaimMailbox(mailboxId: string): Promise<ApiResponse<{ mailboxId: string }>> {
    return this.request(`/mailboxes/${mailboxId}/reclaim`, {
      method: 'POST'
    });
  }

  async searchMailboxNames(query: string): Promise<ApiResponse<{
    query: string;
    available: boolean;
    suggestions: string[];
  }>> {
    return this.request(`/mailboxes/search/${encodeURIComponent(query)}`);
  }

  // Emails
  async getEmails(
    mailboxId: string,
    page = 1,
    limit = 50,
    mailboxAccessToken?: string,
    includeBody = false
  ): Promise<ApiResponse<{ emails: Email[]; pagination: any; mailbox: { id: string; name: string } }>> {
    const headers: HeadersInit = mailboxAccessToken
      ? { 'x-mailbox-access-token': mailboxAccessToken }
      : {};
    const includeBodyParam = includeBody ? '&includeBody=1' : '&includeBody=0';
    return this.request(`/emails/mailbox/${mailboxId}?page=${page}&limit=${limit}${includeBodyParam}`, {
      headers
    });
  }

  async getEmail(
    emailId: string,
    mailboxAccessToken?: string,
    bodyMode: 'both' | 'text' | 'html' = 'both'
  ): Promise<ApiResponse<{ email: Email }>> {
    const headers: HeadersInit = mailboxAccessToken
      ? { 'x-mailbox-access-token': mailboxAccessToken }
      : {};
    return this.request(`/emails/${emailId}?bodyMode=${encodeURIComponent(bodyMode)}`, { headers });
  }

  async markEmailAsRead(
    emailId: string,
    isRead: boolean
  ): Promise<ApiResponse<void>> {
    return this.request(`/emails/${emailId}/read`, {
      method: 'PATCH',
      body: JSON.stringify({ isRead }),
    });
  }

  async deleteEmail(emailId: string): Promise<ApiResponse<void>> {
    return this.request(`/emails/${emailId}`, {
      method: 'DELETE',
    });
  }

  async searchEmails(
    mailboxId: string,
    query: string,
    page = 1,
    limit = 20
  ): Promise<ApiResponse<PaginationData<Email> & { query: string; mailbox: { id: string; name: string } }>> {
    return this.request(
      `/emails/mailbox/${mailboxId}/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`
    );
  }

  async getEmailStats(mailboxId: string): Promise<ApiResponse<{
    mailbox: { id: string; name: string };
    stats: {
      totalEmails: number;
      readEmails: number;
      unreadEmails: number;
      latestEmail?: string;
      oldestEmail?: string;
    };
    topSenders: Array<{
      email: string;
      name?: string;
      count: number;
    }>;
  }>> {
    return this.request(`/emails/mailbox/${mailboxId}/stats`);
  }

  // Admin
  async getAdminDashboard(): Promise<ApiResponse<{
    stats: {
      users: any;
      mailboxes: any;
      emails: any;
    };
    recent: {
      users: any[];
      mailboxes: any[];
    };
  }>> {
    return this.request('/admin/dashboard');
  }

  async getAdminAiStatus(force = false): Promise<ApiResponse<{ ai: AiStatus }>> {
    return this.request(`/admin/ai-status${force ? '?force=1' : ''}`);
  }

  async getAdminUsers(
    search = '',
    page = 1,
    limit = 50
  ): Promise<ApiResponse<PaginationData<User>>> {
    return this.request(
      `/admin/users?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`
    );
  }

  async approveUser(userId: number, isApproved: boolean): Promise<ApiResponse<void>> {
    return this.request(`/admin/users/${userId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved: isApproved }),
    });
  }

  async changeUserPassword(
    userId: number,
    newPassword: string
  ): Promise<ApiResponse<void>> {
    return this.request(`/admin/users/${userId}/password`, {
      method: 'POST',
      body: JSON.stringify({ password: newPassword }),
    });
  }

  async deleteUser(userId: number): Promise<ApiResponse<void>> {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async getUserChildren(kingUserId: number): Promise<ApiResponse<{
    children: Array<{
      id: number;
      username: string;
      telegramId?: string | null;
      color?: string | null;
    }>;
  }>> {
    return this.request(`/admin/users/${kingUserId}/children`);
  }

  async linkUserChild(kingUserId: number, childUserId: number, color?: string): Promise<ApiResponse<{ color?: string }>> {
    return this.request(`/admin/users/${kingUserId}/children`, {
      method: 'POST',
      body: JSON.stringify({ childUserId, color }),
    });
  }

  async unlinkUserChild(kingUserId: number, childUserId: number): Promise<ApiResponse<void>> {
    return this.request(`/admin/users/${kingUserId}/children/${childUserId}`, {
      method: 'DELETE',
    });
  }

  async getTelegramChildren(kingTelegramId: string): Promise<ApiResponse<{
    children: Array<{
      id: number;
      telegramId?: string | null;
      username: string;
      color?: string | null;
      telegramProfile?: {
        username?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        photoUrl?: string | null;
      } | null;
    }>;
  }>> {
    return this.request(`/admin/telegram-children/${encodeURIComponent(kingTelegramId)}`);
  }

  async linkTelegramChild(kingTelegramId: string, childTelegramId: string): Promise<ApiResponse<{ color?: string }>> {
    return this.request('/admin/telegram-children', {
      method: 'POST',
      body: JSON.stringify({ kingTelegramId, childTelegramId }),
    });
  }

  async unlinkTelegramChild(kingTelegramId: string, childTelegramId: string): Promise<ApiResponse<void>> {
    return this.request('/admin/telegram-children', {
      method: 'DELETE',
      body: JSON.stringify({ kingTelegramId, childTelegramId }),
    });
  }

  async deactivateTelegramChild(kingTelegramId: string, childTelegramId: string): Promise<ApiResponse<void>> {
    return this.request('/admin/telegram-children/deactivate', {
      method: 'POST',
      body: JSON.stringify({ kingTelegramId, childTelegramId }),
    });
  }

  async activateTelegramChild(kingTelegramId: string, childTelegramId: string): Promise<ApiResponse<void>> {
    return this.request('/admin/telegram-children/activate', {
      method: 'POST',
      body: JSON.stringify({ kingTelegramId, childTelegramId }),
    });
  }

  async getAdminMailboxes(
    search = '',
    page = 1,
    limit = 50,
    sortBy: 'created_at' | 'owner_username' | 'owner_telegram_id' = 'created_at',
    sortDir: 'asc' | 'desc' = 'desc',
    ownerIds: number[] = []
  ): Promise<ApiResponse<{
    mailboxes: Array<Mailbox & {
      account?: string;
      owner: {
        id: number;
        username: string;
        telegramId?: string | null;
        telegramProfile?: {
          username?: string | null;
          firstName?: string | null;
          lastName?: string | null;
          photoUrl?: string | null;
        } | null;
      };
    }>;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>> {
    const q = search;
    const ownerIdsParam = ownerIds.length ? `&ownerIds=${encodeURIComponent(ownerIds.join(','))}` : '';
    return this.request(
      `/admin/mailboxes?q=${encodeURIComponent(q)}&page=${page}&limit=${limit}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}${ownerIdsParam}`
    );
  }

  async getAdminMailboxOwners(): Promise<ApiResponse<{
    owners: Array<{
      id: number;
      username: string;
      telegramId?: string | null;
      mailboxCount: number;
      telegramProfile?: {
        username?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        photoUrl?: string | null;
      } | null;
    }>;
  }>> {
    return this.request('/admin/mailbox-owners');
  }

  async getTelegramWhitelist(): Promise<ApiResponse<{
    whitelist: TelegramWhitelistItem[];
  }>> {
    return this.request('/admin/telegram-whitelist');
  }

  async getTelegramWhitelistItems(): Promise<ApiResponse<{ whitelist: TelegramWhitelistItem[] }>> {
    return this.request('/admin/telegram-whitelist');
  }

  async addTelegramWhitelistItem(telegramId: string): Promise<ApiResponse<void>> {
    return this.request('/admin/telegram-whitelist/items', {
      method: 'POST',
      body: JSON.stringify({ telegramId })
    });
  }

  async deleteTelegramWhitelistItem(telegramId: string): Promise<ApiResponse<void>> {
    return this.request(`/admin/telegram-whitelist/items/${encodeURIComponent(telegramId)}`, {
      method: 'DELETE'
    });
  }

  async refreshTelegramWhitelistItem(telegramId: string): Promise<ApiResponse<void>> {
    return this.request(`/admin/telegram-whitelist/items/${encodeURIComponent(telegramId)}/refresh`, {
      method: 'POST'
    });
  }

  async transferTelegramId(fromTelegramId: string, toTelegramId: string): Promise<ApiResponse<{
    movedMailboxesCount: number;
    detachedUserId?: number | null;
    oldUserId: number;
  }>> {
    return this.request('/admin/telegram-transfer', {
      method: 'POST',
      body: JSON.stringify({ fromTelegramId, toTelegramId })
    });
  }

  async shareMailboxesByTelegram(
    targetTelegramId: string,
    sourceTelegramId: string
  ): Promise<ApiResponse<{
    targetUserId: number;
    sourceUserId: number;
    sharedNow: number;
    sourceActiveMailboxes: number;
  }>> {
    return this.request('/admin/telegram-share-mailboxes', {
      method: 'POST',
      body: JSON.stringify({ targetTelegramId, sourceTelegramId })
    });
  }

  async deleteAdminMailbox(mailboxId: string): Promise<ApiResponse<{
    deletedEmails: number;
    mailboxName: string;
  }>> {
    return this.request(`/admin/mailboxes/${mailboxId}`, {
      method: 'DELETE',
    });
  }

  async updateTelegramWhitelist(telegramIds: string[]): Promise<ApiResponse<void>> {
    return this.request('/admin/telegram-whitelist', {
      method: 'POST',
      body: JSON.stringify({ telegramIds }),
    });
  }

  async getAdminSettings(): Promise<ApiResponse<{
    settings: Record<string, { value: string; description: string }>;
  }>> {
    return this.request('/admin/settings');
  }

  async updateAdminSetting(
    settingKey: string,
    value: string
  ): Promise<ApiResponse<void>> {
    return this.request(`/admin/settings/${settingKey}`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    });
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.token;
  }

  getToken(): string | null {
    return this.token;
  }

  refreshToken(): void {
    this.loadToken();
  }
}

// Create and export API client instance
export const api = new ApiClient(API_BASE_URL);
export default api;
